/**
 * Custom Destinations — localStorage CRUD + Nominatim geocoding.
 */

const STORAGE_KEY = 'auxilio-travel-custom-destinations';

export interface DestinationEnrichment {
  weather: Array<{
    month: string;
    avg_high_c: number;
    avg_low_c: number;
    rainfall_mm: number;
    rainy_days: number;
  }>;
  pois: Record<string, Array<{
    name: string;
    description?: string;
    rating?: number;
    price_level?: string;
  }>>;
  safety_rating?: string;
  safety_notes?: string;
  average_costs: Record<string, number>;
  visa_info?: string;
  power_info: Record<string, any>;
  internet_info?: string;
  languages: string[];
  currency?: string;
  capital?: string;
  timezones: string[];
  flags: Record<string, string>;
  climate_type?: string;
  best_time_to_visit?: { months: string[]; reason: string };
  tagline?: string;
  budget_level?: string;
  tourism_board_url?: string;
  wikipedia_url?: string;
  travel_advisory_url?: string;
  enrichment_status: Record<string, string>;
}

export interface CustomDestination {
  id: string;
  city: string;
  country: string;
  country_code: string;
  region: string;
  lat: number;
  lon: number;
  tagline: string;
  budget_level?: string;
  is_custom: true;
  created_at: string;
  updated_at: string;
  enrichment?: DestinationEnrichment;
}

/** Shape compatible with DestinationResult used in map/search/cards */
export interface DestinationResult {
  id: string;
  city: string;
  country: string;
  country_code: string;
  region: string;
  lat: number;
  lon: number;
  tagline: string;
  budget_level?: string;
}

// ── ID Generation ──

const slug = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export const generateCustomId = (city: string, country: string): string =>
  `custom-${slug(city)}-${slug(country)}`;

export const isCustomDestination = (id: string): boolean =>
  id.startsWith('custom-');

// ── localStorage CRUD ──

export const loadCustomDestinations = (): CustomDestination[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const saveCustomDestinations = (destinations: CustomDestination[]): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(destinations));
};

export const addCustomDestination = (
  data: Omit<CustomDestination, 'id' | 'is_custom' | 'created_at' | 'updated_at'>
): CustomDestination => {
  const destinations = loadCustomDestinations();
  const now = new Date().toISOString();
  const dest: CustomDestination = {
    ...data,
    id: generateCustomId(data.city, data.country),
    is_custom: true,
    created_at: now,
    updated_at: now,
  };
  // Avoid duplicate IDs — overwrite if same city+country
  const filtered = destinations.filter((d) => d.id !== dest.id);
  filtered.push(dest);
  saveCustomDestinations(filtered);
  return dest;
};

export const updateCustomDestination = (
  id: string,
  updates: Partial<Omit<CustomDestination, 'id' | 'is_custom' | 'created_at'>>
): CustomDestination | null => {
  const destinations = loadCustomDestinations();
  const idx = destinations.findIndex((d) => d.id === id);
  if (idx === -1) return null;

  const updated: CustomDestination = {
    ...destinations[idx],
    ...updates,
    id: updates.city && updates.country
      ? generateCustomId(updates.city, updates.country)
      : destinations[idx].id,
    updated_at: new Date().toISOString(),
  };
  destinations[idx] = updated;
  saveCustomDestinations(destinations);
  return updated;
};

export const deleteCustomDestination = (id: string): void => {
  const destinations = loadCustomDestinations();
  saveCustomDestinations(destinations.filter((d) => d.id !== id));
};

// ── Geocoding via OpenStreetMap Nominatim ──

export interface GeocodeResult {
  lat: number;
  lon: number;
  country_code: string;
  display_name: string;
}

export const geocodeCity = async (
  city: string,
  country: string
): Promise<GeocodeResult | null> => {
  try {
    const q = encodeURIComponent(`${city}, ${country}`);
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'AuxilioTravel/1.0 (travel planning app)',
        },
      }
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || data.length === 0) return null;

    const result = data[0];
    return {
      lat: parseFloat(result.lat),
      lon: parseFloat(result.lon),
      country_code: (result.address?.country_code || '').toUpperCase(),
      display_name: result.display_name || '',
    };
  } catch {
    return null;
  }
};

// ── Enrichment Update ──

export const updateCustomDestinationEnrichment = (
  id: string,
  enrichment: DestinationEnrichment,
  tagline?: string,
  budgetLevel?: string
): CustomDestination | null => {
  const destinations = loadCustomDestinations();
  const idx = destinations.findIndex((d) => d.id === id);
  if (idx === -1) return null;

  const dest = destinations[idx];
  dest.enrichment = enrichment;
  if (tagline && !dest.tagline) dest.tagline = tagline;
  if (budgetLevel && !dest.budget_level) dest.budget_level = budgetLevel;
  dest.updated_at = new Date().toISOString();
  destinations[idx] = dest;
  saveCustomDestinations(destinations);
  return dest;
};

// ── Convert to DestinationResult ──

export const toDestinationResult = (custom: CustomDestination): DestinationResult => ({
  id: custom.id,
  city: custom.city,
  country: custom.country,
  country_code: custom.country_code,
  region: custom.region,
  lat: custom.lat,
  lon: custom.lon,
  tagline: custom.tagline,
  budget_level: custom.budget_level,
});
